import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { submissionAPI } from '../services/api';
import { FormattedText } from '../utils/formatText';
import CodeDragDropQuestion from './CodeDragDropQuestion';
import CodeDragDropResults from './CodeDragDropResults';
import { useToast } from './Toast';
import { addSubmission } from '../store/submissionsSlice';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReportIssueButton from './ReportIssueButton';

// Add animation styles for timer
const pulseAnimation = `
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(1.05);
    }
  }
`;

// Inject the animation styles
if (typeof document !== 'undefined') {
  const styleId = 'quiz-timer-animation';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = pulseAnimation;
    document.head.appendChild(style);
  }
}

function TakeQuiz({ quiz, onComplete, isDarkMode = false, embedded = false, isRetake = false, attemptNumber = 1, retakeShuffledOptions = {} }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storageKey = `quiz_progress_${quiz._id}_attempt_${attemptNumber}`;
  const { showToast } = useToast();
  
  // Initialize state from localStorage if available
  const getInitialState = () => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Verify it's for the same quiz
        if (parsed.quizId === quiz._id) {
          return parsed;
        }
      }
    } catch (error) {
      console.error('Error loading saved progress:', error);
    }
    return null;
  };

  const initialState = getInitialState();
  
  const [currentQuestion, setCurrentQuestion] = useState(initialState?.currentQuestion || 0);
  const [answers, setAnswers] = useState(initialState?.answers || {});
  const [hintsUnlocked, setHintsUnlocked] = useState(initialState?.hintsUnlocked || {});
  const [timeLeft, setTimeLeft] = useState(initialState?.timeLeft || quiz.duration * 60);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [shuffledOptions, setShuffledOptions] = useState(initialState?.shuffledOptions || {});
  const [showingFeedback, setShowingFeedback] = useState(false);
  const [currentQuestionResult, setCurrentQuestionResult] = useState(null);

  // Shuffle options for all questions on component mount
  useEffect(() => {
    // If this is a retake and we have shuffled options from the first attempt, use those
    if (isRetake && Object.keys(retakeShuffledOptions).length > 0) {
      setShuffledOptions(retakeShuffledOptions);
      return;
    }
    
    // Only shuffle if we don't have saved shuffled options
    if (Object.keys(shuffledOptions).length > 0) {
      return; // Use the restored shuffled options
    }
    
    const shuffled = {};
    quiz.questions.forEach((question) => {
      // Create array of {option, originalIndex} pairs
      const optionsWithIndex = question.options.map((option, index) => ({
        option,
        originalIndex: index
      }));
      
      // Shuffle the array
      const shuffledArray = [...optionsWithIndex].sort(() => Math.random() - 0.5);
      
      shuffled[question._id] = shuffledArray;
    });
    setShuffledOptions(shuffled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quiz, isRetake, retakeShuffledOptions]);

  // Save progress to localStorage whenever state changes
  useEffect(() => {
    if (!submitted) {
      const progressData = {
        quizId: quiz._id,
        currentQuestion,
        answers,
        hintsUnlocked,
        timeLeft,
        shuffledOptions
      };
      localStorage.setItem(storageKey, JSON.stringify(progressData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, answers, hintsUnlocked, timeLeft, submitted, quiz._id, storageKey]);

  useEffect(() => {
    if (submitted) return;

    // Request fullscreen when quiz starts
    const enterFullscreen = async () => {
      try {
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) { /* Safari */
          await elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) { /* IE11 */
          await elem.msRequestFullscreen();
        }
      } catch (error) {
        console.log('Fullscreen request failed:', error);
      }
    };
    enterFullscreen();

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted]);

  const handleAnswerSelect = (questionId, answer) => {
    // answer can be: originalIndex for MCQ/TrueFalse, or string for FillInBlank/Essay
    setAnswers({
      ...answers,
      [questionId]: answer,
    });
    
    // Clear feedback if user changes answer
    if (showingFeedback) {
      setShowingFeedback(false);
      setCurrentQuestionResult(null);
    }
  };

  const checkCurrentAnswer = () => {
    const currentQ = quiz.questions[currentQuestion];
    const userAnswer = answers[currentQ._id];
    
    let isCorrect = false;
    
    if (currentQ.questionType === 'FillInBlank') {
      // Case-insensitive comparison for fill-in-blank
      isCorrect = userAnswer?.trim().toLowerCase() === currentQ.correctAnswer?.toString().trim().toLowerCase();
    } else if (currentQ.questionType === 'MCQ' || currentQ.questionType === 'TrueFalse' || currentQ.questionType === 'CodeSnippet') {
      // Compare selected option index
      isCorrect = userAnswer === currentQ.correctAnswer;
    } else if (currentQ.questionType === 'CodeDragDrop') {
      // For CodeDragDrop, check if all blanks are filled correctly
      // userAnswer is an object like { 1: "answer1", 2: "answer2" }
      isCorrect = currentQ.blanks?.every(blank => {
        const userBlankAnswer = userAnswer?.[blank.id];
        return userBlankAnswer === blank.correctAnswer;
      });
    }
    // Note: Essay questions won't show immediate feedback as they require manual grading
    
    setCurrentQuestionResult({
      isCorrect,
      question: currentQ,
      userAnswer,
      explanation: currentQ.explanation
    });
    setShowingFeedback(true);
  };

  const handleNextQuestion = () => {
    const currentQ = quiz.questions[currentQuestion];
    
    // Check if we're in immediate feedback mode
    if (quiz.showAnswersMode === 'immediate' && !showingFeedback) {
      // First click: check answer and show feedback (unless it's an essay question)
      if (currentQ.questionType === 'Essay') {
        // Skip feedback for essay questions, just move to next
        setCurrentQuestion(currentQuestion + 1);
      } else {
        if (answers[currentQ._id] === undefined) {
          if (!window.confirm('⚠️ You haven\'t answered this question yet. Do you want to proceed to the next question?')) {
            return;
          }
        }
        checkCurrentAnswer();
      }
    } else {
      // Second click (continue) or not in immediate mode: move to next question
      setShowingFeedback(false);
      setCurrentQuestionResult(null);
      
      if (!showingFeedback && answers[currentQ._id] === undefined) {
        if (!window.confirm('⚠️ You haven\'t answered this question yet. Do you want to proceed to the next question?')) {
          return;
        }
      }
      
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const unlockHint = (questionId, hintIndex) => {
    const currentHints = hintsUnlocked[questionId] || [];
    if (!currentHints.includes(hintIndex)) {
      setHintsUnlocked({
        ...hintsUnlocked,
        [questionId]: [...currentHints, hintIndex]
      });
    }
  };

  const handleSubmit = async () => {
    if (submitting) return; // Prevent double submission
    
    setLoading(true);
    setSubmitting(true);

    const formattedAnswers = Object.keys(answers).map((questionId) => ({
      questionId,
      selectedAnswer: answers[questionId],
      hintsUsed: hintsUnlocked[questionId] || []
    }));

    try {
      const response = await submissionAPI.submitQuiz({
        quizId: quiz._id,
        answers: formattedAnswers,
        shuffledOptions: shuffledOptions
      });
      
      const submissionData = response.data.result;
      setResult(submissionData);
      setSubmitted(true);
      
      // Exit fullscreen after submission
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) { /* Safari */
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) { /* IE11 */
          await document.msExitFullscreen();
        }
      } catch (error) {
        console.log('Exit fullscreen failed:', error);
      }
      
      // Clear saved progress after successful submission
      localStorage.removeItem(storageKey);
      
      // Create submission object for Redux store
      const newSubmission = {
        _id: submissionData.submissionId,
        quiz: quiz,
        score: submissionData.score,
        totalQuestions: submissionData.totalQuestions,
        percentage: submissionData.percentage,
        attemptNumber: submissionData.attemptNumber,
        isPractice: submissionData.isPractice,
        submittedAt: new Date().toISOString(),
        detailedResults: submissionData.detailedResults,
        percentile: submissionData.percentile,
        totalParticipants: submissionData.totalParticipants
      };
      
      // Add submission to Redux store
      dispatch(addSubmission(newSubmission));
      
      // Check if results should be shown to students
      if (quiz.showResultsToStudents === false) {
        // Don't show results, just return to dashboard with a message
        alert('Quiz submitted successfully! Your instructor will review your answers.');
        onComplete();
        return;
      }
      
      // If embedded, show results inline; otherwise navigate to results page
      if (embedded) {
        // Results will be shown inline - component handles this with submitted state
      } else {
        // Navigate to results page with data
        navigate('/quiz-results', {
          state: {
            result: submissionData,
            quiz: quiz
          }
        });
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
      const errorData = error.response?.data;
      
      // Check if quiz already taken
      if (errorData?.alreadyTaken) {
        alert('You have already submitted this quiz. Multiple attempts are not allowed.');
        onComplete(); // Return to dashboard
        return;
      }
      
      const errorMessage = errorData?.error || 
                          errorData?.message || 
                          error.message || 
                          'Failed to submit quiz';
      alert(`Error submitting quiz: ${errorMessage}`);
      setSubmitting(false); // Re-enable only on error
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // After submission, navigate to results page
  // No need to render results here

  const progress = ((currentQuestion + 1) / quiz.questions.length) * 100;
  const currentQ = quiz.questions[currentQuestion];
  const currentShuffledOptions = shuffledOptions[currentQ._id] || [];

  // Show inline results when embedded and submitted
  if (submitted && embedded && result) {
    const passed = result.percentage >= (quiz.passingPercentage || 40);
    
    return (
      <div style={{ 
        background: 'transparent',
        padding: 0
      }}>
        <div style={{
          background: isDarkMode ? '#2d3748' : '#ffffff',
          color: isDarkMode ? '#e2e8f0' : '#1a202c',
          padding: '24px',
          borderRadius: '8px'
        }}>
          {result.attemptNumber > 1 && (
            <div style={{
              marginBottom: '24px',
              padding: '16px 20px',
              backgroundColor: isDarkMode ? '#2d3748' : '#fef3c7',
              border: `2px solid ${isDarkMode ? '#d69e2e' : '#f59e0b'}`,
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: isDarkMode ? '#fbd38d' : '#92400e' }}>
                🔄 PRACTICE MODE - Practice Attempt #{result.attemptNumber - 1}
              </p>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: isDarkMode ? '#d69e2e' : '#78350f' }}>
                This score is for practice only and won't affect your ranking. Your first attempt counts for the leaderboard.
              </p>
            </div>
          )}
          
          <h2 style={{ color: isDarkMode ? '#90cdf4' : '#667eea', marginBottom: '24px', textAlign: 'center', fontSize: '24px' }}>
            🎉 Quiz Completed!
          </h2>
          
          <div style={{ 
            padding: '32px', 
            backgroundColor: passed ? (isDarkMode ? '#1c4532' : '#c6f6d5') : (isDarkMode ? '#4c1d1d' : '#fed7d7'),
            borderRadius: '12px',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            <h1 style={{ 
              fontSize: '48px', 
              margin: '0',
              color: passed ? (isDarkMode ? '#68d391' : '#2f855a') : (isDarkMode ? '#fc8181' : '#c53030')
            }}>
              {result.percentage}%
            </h1>
            <p style={{ 
              fontSize: '18px', 
              marginTop: '8px',
              color: passed ? (isDarkMode ? '#68d391' : '#2f855a') : (isDarkMode ? '#fc8181' : '#c53030'),
              fontWeight: '600'
            }}>
              {passed ? '🎉 Congratulations!' : '💪 Keep Learning!'}
            </p>
          </div>

          <div style={{ marginBottom: '24px', fontSize: '14px', textAlign: 'center', color: isDarkMode ? '#a0aec0' : '#4a5568' }}>
            <p>
              You answered <strong style={{ color: isDarkMode ? '#90cdf4' : '#667eea' }}>{result.score}</strong> out of{' '}
              <strong style={{ color: isDarkMode ? '#90cdf4' : '#667eea' }}>{result.totalQuestions}</strong> questions correctly
            </p>
          </div>

          {result.percentile !== undefined && (
            <div style={{
              marginBottom: '24px',
              padding: '16px',
              backgroundColor: isDarkMode ? '#1a202c' : '#f0f4ff',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, fontSize: '14px', color: isDarkMode ? '#a0aec0' : '#4a5568' }}>
                You scored better than <strong style={{ color: isDarkMode ? '#90cdf4' : '#667eea', fontSize: '16px' }}>{result.percentile}%</strong> of participants
              </p>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: isDarkMode ? '#718096' : '#718096' }}>
                Total participants: {result.totalParticipants}
              </p>
            </div>
          )}

          {result.detailedResults && result.detailedResults.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ 
                marginBottom: '20px', 
                color: isDarkMode ? '#90cdf4' : '#2d3748', 
                fontSize: '20px', 
                fontWeight: '700',
                borderBottom: `3px solid ${isDarkMode ? '#90cdf4' : '#667eea'}`,
                paddingBottom: '12px'
              }}>
                🔍 Review Your Answers
              </h3>
              {result.detailedResults.map((item, index) => {
                if (!item || !item.question) return null;
                
                const question = item.question;
                
                return (
                  <div 
                    key={item.questionId} 
                    style={{ 
                      marginBottom: '20px',
                      padding: '0',
                      border: 'none',
                      borderRadius: '12px',
                      backgroundColor: isDarkMode ? '#1a202c' : '#ffffff',
                      boxShadow: isDarkMode ? 'none' : '0 2px 8px rgba(0,0,0,0.08)',
                      overflow: 'hidden'
                    }}
                  >
                    <div style={{ 
                      padding: '20px',
                      backgroundColor: item.isCorrect 
                        ? (isDarkMode ? '#1c4532' : '#f0fdf4') 
                        : (isDarkMode ? '#4c1d1d' : '#fef2f2'),
                      borderLeft: `6px solid ${item.isCorrect ? (isDarkMode ? '#68d391' : '#22c55e') : (isDarkMode ? '#fc8181' : '#ef4444')}`
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ 
                            fontSize: '11px', 
                            fontWeight: '700', 
                            color: item.isCorrect ? (isDarkMode ? '#68d391' : '#15803d') : (isDarkMode ? '#fc8181' : '#b91c1c'),
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Question {index + 1} {item.isCorrect ? '✓ CORRECT' : '✗ INCORRECT'}
                          </span>
                        </div>
                        <ReportIssueButton
                          sourceType="quiz"
                          sourceId={quiz._id}
                          questionPath={`questions.${index}`}
                          questionId={question._id}
                          questionIndex={index}
                          questionData={{
                            question: question.question,
                            options: question.options,
                            correctAnswer: question.correctAnswer,
                            questionType: question.questionType,
                            explanation: question.explanation,
                            difficulty: question.difficulty,
                            tags: question.tags,
                            category: question.category
                          }}
                          metadata={{
                            sourceName: quiz.title,
                            sourceCode: quiz.code,
                            questionNumber: index + 1,
                            totalQuestions: quiz.questions?.length || 0,
                            userAnswer: item.selectedAnswer,
                            isCorrect: item.isCorrect,
                            attemptNumber: result.attemptNumber,
                            submissionId: result.submissionId,
                            reportedFrom: 'quiz-feedback',
                            timestamp: new Date().toISOString(),
                            groupName: quiz.group?.name,
                            groupId: quiz.group?._id
                          }}
                          size="icon-only"
                        />
                      </div>
                      
                      <div style={{ marginBottom: '16px' }}>
                        <FormattedText text={question.question} style={{ 
                          fontSize: '15px', 
                          fontWeight: '600',
                          color: isDarkMode ? '#e2e8f0' : '#1a202c',
                          lineHeight: '1.6'
                        }} />
                      </div>

                      {/* Code Drag & Drop Question Results */}
                      {question.questionType === 'CodeDragDrop' && item.selectedAnswer ? (
                        <CodeDragDropResults
                          correctCode={question.correctAnswer}
                          userAnswer={item.selectedAnswer}
                          isCorrect={item.isCorrect}
                        />
                      ) : null}

                      {question.questionType !== 'CodeDragDrop' && question.options && Array.isArray(question.options) && (
                        <div style={{ marginTop: '12px' }}>
                          {(() => {
                            const shuffledOptionsForQuestion = result.shuffledOptions?.[question._id];
                            const displayOptions = shuffledOptionsForQuestion || 
                              question.options.map((option, index) => ({ option, originalIndex: index }));
                            
                            return displayOptions.map((optionItem, displayIdx) => {
                              const option = typeof optionItem === 'string' ? optionItem : optionItem.option;
                              const optIdx = typeof optionItem === 'string' ? displayIdx : optionItem.originalIndex;
                              const isUserAnswer = optIdx === item.selectedAnswer;
                              const isCorrectAnswer = optIdx === item.correctAnswer;
                            
                              let backgroundColor = isDarkMode ? '#2d3748' : '#ffffff';
                              let borderColor = isDarkMode ? '#4a5568' : '#e2e8f0';
                              let textColor = isDarkMode ? '#e2e8f0' : '#2d3748';
                              let icon = '';
                              let fontWeight = 'normal';
                              
                              if (isCorrectAnswer) {
                                backgroundColor = isDarkMode ? '#1c4532' : '#d1fae5';
                                borderColor = isDarkMode ? '#68d391' : '#10b981';
                                textColor = isDarkMode ? '#68d391' : '#065f46';
                                icon = '✓';
                                fontWeight = '600';
                              }
                              
                              if (isUserAnswer && !item.isCorrect) {
                                backgroundColor = isDarkMode ? '#4c1d1d' : '#fee2e2';
                                borderColor = isDarkMode ? '#fc8181' : '#ef4444';
                                textColor = isDarkMode ? '#fc8181' : '#991b1b';
                                icon = '✗';
                                fontWeight = '600';
                              }
                              
                              return (
                                <div 
                                  key={displayIdx}
                                  style={{
                                    padding: '12px 16px',
                                    marginBottom: '8px',
                                    backgroundColor,
                                    border: `2px solid ${borderColor}`,
                                    borderRadius: '8px',
                                    color: textColor,
                                    fontWeight,
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                    <span style={{ fontWeight: '700', minWidth: '20px' }}>
                                      {String.fromCharCode(65 + displayIdx)}.
                                    </span>
                                    <FormattedText text={option} />
                                  </div>
                                  {icon && <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{icon}</span>}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      )}

                      {/* Essay and Fill-in-Blank Answers */}
                      {(question.questionType === 'Essay' || question.questionType === 'FillInBlank') && (
                        <div style={{ marginTop: '12px' }}>
                          <div style={{
                            padding: '16px',
                            backgroundColor: isDarkMode ? '#1a202c' : '#f0f9ff',
                            borderRadius: '8px',
                            border: `2px solid ${isDarkMode ? '#4299e1' : '#3b82f6'}`
                          }}>
                            <strong style={{ 
                              fontSize: '12px', 
                              color: isDarkMode ? '#63b3ed' : '#1e40af', 
                              textTransform: 'uppercase',
                              fontWeight: '700'
                            }}>
                              📝 Your Answer:
                            </strong>
                            <p style={{ 
                              margin: '8px 0 0 0', 
                              color: isDarkMode ? '#e2e8f0' : '#1e3a8a',
                              fontSize: '14px',
                              lineHeight: '1.6',
                              fontWeight: '500'
                            }}>
                              {item.selectedAnswer || 'No answer provided'}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Explanation */}
                      {question.explanation && (
                        <div style={{
                          marginTop: '16px',
                          padding: '12px 16px',
                          backgroundColor: isDarkMode ? '#1a365d' : '#eff6ff',
                          borderLeft: `4px solid ${isDarkMode ? '#4299e1' : '#3b82f6'}`,
                          borderRadius: '4px'
                        }}>
                          <strong style={{ fontSize: '12px', color: isDarkMode ? '#63b3ed' : '#1e40af', textTransform: 'uppercase' }}>
                            💡 Explanation:
                          </strong>
                          <FormattedText text={question.explanation} style={{ 
                            marginTop: '8px', 
                            color: isDarkMode ? '#e2e8f0' : '#1e3a8a',
                            fontSize: '13px',
                            lineHeight: '1.6'
                          }} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <button
              onClick={onComplete}
              style={{
                padding: '12px 32px',
                fontSize: '14px',
                backgroundColor: isDarkMode ? '#90cdf4' : '#667eea',
                color: isDarkMode ? '#1a202c' : '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              ← Back to Course
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ 
      background: embedded ? 'transparent' : undefined,
      padding: embedded ? 0 : undefined 
    }}>
      <div className="card" style={{
        background: isDarkMode ? '#2d3748' : '#ffffff',
        color: isDarkMode ? '#e2e8f0' : '#1a202c',
        boxShadow: embedded ? 'none' : undefined,
        border: embedded ? 'none' : undefined
      }}>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <h2 style={{ color: isDarkMode ? '#90cdf4' : '#667eea', fontSize: '20px', margin: 0 }}>{quiz.title}</h2>
              {isRetake && attemptNumber > 1 && (
                <div style={{
                  marginTop: '6px',
                  padding: '4px 12px',
                  background: isDarkMode ? '#38b2ac' : '#e6fffa',
                  color: isDarkMode ? '#e6fffa' : '#234e52',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  display: 'inline-block',
                  border: `1.5px solid ${isDarkMode ? '#4fd1c5' : '#81e6d9'}`
                }}>
                  🔄 Practice Attempt #{attemptNumber - 1}
                </div>
              )}
            </div>
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: '4px'
            }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: isDarkMode ? '#a0aec0' : '#718096',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Time Remaining
              </div>
              <div style={{ 
                fontSize: '24px', 
                fontWeight: '700',
                color: timeLeft < 60 ? '#f56565' : (timeLeft < 300 ? '#f59e0b' : (isDarkMode ? '#90cdf4' : '#667eea')),
                padding: '10px 20px',
                background: timeLeft < 60 ? '#fed7d7' : (timeLeft < 300 ? '#fef3c7' : (isDarkMode ? '#1a202c' : '#eef2ff')),
                borderRadius: '10px',
                border: `2px solid ${timeLeft < 60 ? '#f56565' : (timeLeft < 300 ? '#f59e0b' : (isDarkMode ? '#4a5568' : '#c7d2fe'))}`,
                boxShadow: timeLeft < 60 ? '0 0 20px rgba(245, 101, 101, 0.3)' : 'none',
                animation: timeLeft < 60 ? 'pulse 1s infinite' : 'none',
                minWidth: '100px',
                textAlign: 'center'
              }}>
                ⏱️ {formatTime(timeLeft)}
              </div>
              {timeLeft < 60 && (
                <div style={{
                  fontSize: '10px',
                  fontWeight: '600',
                  color: '#f56565',
                  animation: 'pulse 1s infinite'
                }}>
                  ⚠️ Hurry up!
                </div>
              )}
              {timeLeft >= 60 && timeLeft < 300 && (
                <div style={{
                  fontSize: '10px',
                  fontWeight: '600',
                  color: '#f59e0b'
                }}>
                  ⏰ Time running low
                </div>
              )}
            </div>
          </div>
          
          {/* Tags display */}
          {quiz.tags && quiz.tags.length > 0 && (
            <div style={{ marginBottom: '12px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {quiz.tags.map((tag, idx) => (
                <span
                  key={idx}
                  style={{
                    padding: '3px 10px',
                    background: isDarkMode ? '#1a202c' : 'white',
                    color: isDarkMode ? '#90cdf4' : '#667eea',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '500',
                    border: `1px solid ${isDarkMode ? '#4a5568' : '#c7d2fe'}`
                  }}
                >
                  🏷️ {tag.name || tag}
                </span>
              ))}
            </div>
          )}
          
          <div style={{ 
            height: '6px', 
            backgroundColor: isDarkMode ? '#1a202c' : '#e2e8f0', 
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{ 
              height: '100%', 
              width: `${progress}%`,
              backgroundColor: isDarkMode ? '#90cdf4' : '#667eea',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <p style={{ marginTop: '8px', color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '13px', fontWeight: '500' }}>
            Question {currentQuestion + 1} of {quiz.questions.length}
          </p>

          {/* Question Navigation Indicators */}
          <div style={{ 
            marginTop: '12px', 
            display: 'flex', 
            gap: '6px', 
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            {quiz.questions.map((q, index) => (
              <button
                key={index}
                onClick={() => setCurrentQuestion(index)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '6px',
                  border: index === currentQuestion 
                    ? `2px solid ${isDarkMode ? '#90cdf4' : '#667eea'}` 
                    : `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                  backgroundColor: answers[q._id] !== undefined 
                    ? (isDarkMode ? '#2f855a' : '#c6f6d5') 
                    : (isDarkMode ? '#1a202c' : 'white'),
                  color: answers[q._id] !== undefined 
                    ? (isDarkMode ? '#c6f6d5' : '#22543d') 
                    : (isDarkMode ? '#a0aec0' : '#718096'),
                  fontSize: '12px',
                  fontWeight: index === currentQuestion ? '700' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                {index + 1}
                {answers[q._id] !== undefined && (
                  <span style={{ 
                    position: 'absolute', 
                    top: '-2px', 
                    right: '-2px', 
                    fontSize: '10px' 
                  }}>✓</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Code Reference Section */}
        {quiz.codeReference && quiz.codeReference.showToStudents && quiz.codeReference.code && (
          <div style={{ 
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: isDarkMode ? '#1a202c' : '#f7fafc',
            border: `2px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
            borderRadius: '8px'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '12px',
              paddingBottom: '8px',
              borderBottom: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
            }}>
              <span style={{ fontSize: '16px', marginRight: '8px' }}>💻</span>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: isDarkMode ? '#90cdf4' : '#2d3748' }}>
                Code Reference ({quiz.codeReference.language})
              </h4>
            </div>
            <SyntaxHighlighter
              language={quiz.codeReference.language.toLowerCase()}
              style={vscDarkPlus}
              customStyle={{
                borderRadius: '6px',
                fontSize: '13px',
                margin: 0,
                padding: '16px'
              }}
              showLineNumbers={true}
            >
              {quiz.codeReference.code}
            </SyntaxHighlighter>
          </div>
        )}

        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '16px', lineHeight: '1.6', color: isDarkMode ? '#e2e8f0' : '#2d3748', fontWeight: '600' }}>
            <FormattedText text={currentQ.question} />
          </h3>

          {/* Render based on individual question type */}
          {currentQ.questionType === 'FillInBlank' && (
            <div>
              <input
                type="text"
                value={answers[currentQ._id] || ''}
                onChange={(e) => handleAnswerSelect(currentQ._id, e.target.value)}
                placeholder="Type your answer here..."
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: `2px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: isDarkMode ? '#e2e8f0' : '#2d3748',
                  backgroundColor: isDarkMode ? '#1a202c' : 'white'
                }}
              />
              <small style={{ display: 'block', marginTop: '8px', color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '12px' }}>
                Enter your answer above (case-insensitive)
              </small>
            </div>
          )}

          {currentQ.questionType === 'Essay' && (
            <div>
              {/* Hints Section */}
              {currentQ.hints && currentQ.hints.length > 0 && (
                <div style={{ 
                  marginBottom: '16px',
                  padding: '14px',
                  backgroundColor: isDarkMode ? '#2d3748' : '#fffbeb',
                  border: `1.5px solid ${isDarkMode ? '#d69e2e' : '#fbbf24'}`,
                  borderRadius: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontSize: '16px', marginRight: '6px' }}>💡</span>
                    <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: isDarkMode ? '#fbd38d' : '#92400e' }}>
                      Hints Available ({currentQ.hints.length})
                    </h4>
                  </div>
                  
                  {currentQ.hints.map((hint, hintIndex) => {
                    const isUnlocked = (hintsUnlocked[currentQ._id] || []).includes(hintIndex);
                    
                    return (
                      <div key={hintIndex} style={{ marginBottom: hintIndex < currentQ.hints.length - 1 ? '8px' : '0' }}>
                        {isUnlocked ? (
                          <div style={{
                            padding: '10px 12px',
                            backgroundColor: isDarkMode ? '#1a202c' : '#fef3c7',
                            border: `1px solid ${isDarkMode ? '#d69e2e' : '#fbbf24'}`,
                            borderRadius: '6px'
                          }}>
                            <div style={{ fontSize: '11px', color: isDarkMode ? '#fbd38d' : '#92400e', marginBottom: '4px', fontWeight: '600' }}>
                              Hint {hintIndex + 1} (unlocked - {hint.pointPenalty} point penalty)
                            </div>
                            <div style={{ fontSize: '12px', color: isDarkMode ? '#e2e8f0' : '#78350f', lineHeight: '1.5' }}>
                              {hint.text}
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => unlockHint(currentQ._id, hintIndex)}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              backgroundColor: isDarkMode ? '#1a202c' : 'white',
                              border: `1.5px dashed ${isDarkMode ? '#d69e2e' : '#fbbf24'}`,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              color: isDarkMode ? '#fbd38d' : '#92400e',
                              fontWeight: '600',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = isDarkMode ? '#2d3748' : '#fef3c7';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = isDarkMode ? '#1a202c' : 'white';
                            }}
                          >
                            🔓 Unlock Hint {hintIndex + 1} (costs {hint.pointPenalty} point{hint.pointPenalty > 1 ? 's' : ''})
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              <textarea
                value={answers[currentQ._id] || ''}
                onChange={(e) => handleAnswerSelect(currentQ._id, e.target.value)}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                onDrop={(e) => e.preventDefault()}
                onDragOver={(e) => e.preventDefault()}
                placeholder="Write your answer here..."
                rows="8"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: `2px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: isDarkMode ? '#e2e8f0' : '#2d3748',
                  backgroundColor: isDarkMode ? '#1a202c' : 'white',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
              <small style={{ display: 'block', marginTop: '8px', color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '12px' }}>
                {answers[currentQ._id] ? `${answers[currentQ._id].length} characters` : 'No answer yet'}
              </small>
              <small style={{ display: 'block', marginTop: '4px', color: isDarkMode ? '#fc8181' : '#e53e3e', fontSize: '11px', fontWeight: '500' }}>
                ⚠️ Copy-paste is disabled for essay questions to ensure original work
              </small>
            </div>
          )}

          {currentQ.questionType === 'CodeDragDrop' && (
            <CodeDragDropQuestion
              question={currentQ}
              currentAnswer={answers[currentQ._id]}
              onAnswerChange={(answer) => handleAnswerSelect(currentQ._id, answer)}
            />
          )}

          {(currentQ.questionType === 'MCQ' || currentQ.questionType === 'TrueFalse' || currentQ.questionType === 'CodeSnippet' || !currentQ.questionType) && (
            <div style={{ display: 'grid', gap: '10px' }}>
              {currentShuffledOptions.map((item, displayIndex) => (
                <button
                  key={displayIndex}
                  onClick={() => handleAnswerSelect(currentQ._id, item.originalIndex)}
                  style={{
                    padding: '12px 16px',
                    border: `2px solid ${answers[currentQ._id] === item.originalIndex ? (isDarkMode ? '#90cdf4' : '#667eea') : (isDarkMode ? '#4a5568' : '#e2e8f0')}`,
                    borderRadius: '8px',
                    backgroundColor: answers[currentQ._id] === item.originalIndex ? (isDarkMode ? '#2c5282' : '#eef2ff') : (isDarkMode ? '#1a202c' : 'white'),
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    fontWeight: '500',
                    color: isDarkMode ? '#e2e8f0' : '#2d3748'
                  }}
                  onMouseEnter={(e) => {
                    if (answers[currentQ._id] !== item.originalIndex) {
                      e.currentTarget.style.borderColor = isDarkMode ? '#90cdf4' : '#667eea';
                      e.currentTarget.style.backgroundColor = isDarkMode ? '#2d3748' : '#fafafa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (answers[currentQ._id] !== item.originalIndex) {
                      e.currentTarget.style.borderColor = isDarkMode ? '#4a5568' : '#e2e8f0';
                      e.currentTarget.style.backgroundColor = isDarkMode ? '#1a202c' : 'white';
                    }
                  }}
                >
                  <span style={{ fontWeight: '700', marginRight: '8px', fontSize: '14px' }}>
                    {String.fromCharCode(65 + displayIndex)}.
                  </span>
                  <FormattedText text={item.option} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Immediate Feedback Display */}
        {showingFeedback && currentQuestionResult && (
          <div style={{
            marginTop: '24px',
            padding: '20px',
            borderRadius: '12px',
            border: `3px solid ${currentQuestionResult.isCorrect ? '#48bb78' : '#f56565'}`,
            background: currentQuestionResult.isCorrect 
              ? (isDarkMode ? 'rgba(72, 187, 120, 0.1)' : '#f0fff4')
              : (isDarkMode ? 'rgba(245, 101, 101, 0.1)' : '#fff5f5')
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <span style={{ fontSize: '32px' }}>
                {currentQuestionResult.isCorrect ? '✅' : '❌'}
              </span>
              <h4 style={{ 
                margin: 0, 
                fontSize: '18px', 
                fontWeight: '700',
                color: currentQuestionResult.isCorrect 
                  ? (isDarkMode ? '#48bb78' : '#22543d')
                  : (isDarkMode ? '#fc8181' : '#c53030')
              }}>
                {currentQuestionResult.isCorrect ? 'Correct!' : 'Incorrect'}
              </h4>
            </div>
            
            {/* Show user's answer */}
            {currentQuestionResult.question.questionType !== 'Essay' && (
              <div style={{ 
                marginBottom: '12px',
                padding: '12px',
                background: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
                borderRadius: '8px'
              }}>
                <p style={{ margin: '0 0 6px 0', fontSize: '13px', fontWeight: '600', color: isDarkMode ? '#e2e8f0' : '#2d3748' }}>
                  Your Answer:
                </p>
                {currentQuestionResult.question.questionType === 'FillInBlank' ? (
                  <p style={{ margin: 0, fontSize: '14px', color: isDarkMode ? '#e2e8f0' : '#2d3748', fontWeight: '500' }}>
                    {currentQuestionResult.userAnswer ? String(currentQuestionResult.userAnswer) : '(No answer provided)'}
                  </p>
                ) : currentQuestionResult.question.questionType === 'CodeDragDrop' ? (
                  <div>
                    {currentQuestionResult.question.blanks?.map((blank, idx) => (
                      <div key={idx} style={{ marginBottom: '6px' }}>
                        <span style={{ fontSize: '13px', color: isDarkMode ? '#a0aec0' : '#718096' }}>
                          Blank {blank.id}:
                        </span>
                        <span style={{ 
                          marginLeft: '8px', 
                          fontSize: '14px', 
                          color: isDarkMode ? '#e2e8f0' : '#2d3748', 
                          fontWeight: '500' 
                        }}>
                          {currentQuestionResult.userAnswer?.[blank.id] || '(Empty)'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: '14px', color: isDarkMode ? '#e2e8f0' : '#2d3748', fontWeight: '500' }}>
                    {(currentQuestionResult.userAnswer !== undefined && 
                      currentQuestionResult.userAnswer !== null && 
                      currentQuestionResult.question.options && 
                      currentQuestionResult.question.options[currentQuestionResult.userAnswer])
                      ? <FormattedText text={currentQuestionResult.question.options[currentQuestionResult.userAnswer]} />
                      : '(No answer provided)'}
                  </p>
                )}
              </div>
            )}
            
            {!currentQuestionResult.isCorrect && currentQuestionResult.question.questionType !== 'Essay' && currentQuestionResult.question.questionType !== 'CodeDragDrop' && (
              <div style={{ 
                marginBottom: '12px',
                padding: '12px',
                background: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
                borderRadius: '8px'
              }}>
                <p style={{ margin: '0 0 6px 0', fontSize: '13px', fontWeight: '600', color: isDarkMode ? '#e2e8f0' : '#2d3748' }}>
                  Correct Answer:
                </p>
                <p style={{ margin: 0, fontSize: '14px', color: isDarkMode ? '#90cdf4' : '#2b6cb0', fontWeight: '500' }}>
                  {currentQuestionResult.question.questionType === 'FillInBlank' 
                    ? currentQuestionResult.question.correctAnswer
                    : <FormattedText text={currentQuestionResult.question.options[currentQuestionResult.question.correctAnswer]} />
                  }
                </p>
              </div>
            )}
            
            {!currentQuestionResult.isCorrect && currentQuestionResult.question.questionType === 'CodeDragDrop' && (
              <div style={{ 
                marginBottom: '12px',
                padding: '12px',
                background: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
                borderRadius: '8px'
              }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '600', color: isDarkMode ? '#e2e8f0' : '#2d3748' }}>
                  Correct Answers:
                </p>
                {currentQuestionResult.question.blanks?.map((blank, idx) => (
                  <div key={idx} style={{ marginBottom: '6px' }}>
                    <span style={{ fontSize: '13px', color: isDarkMode ? '#a0aec0' : '#718096' }}>
                      Blank {blank.id}:
                    </span>
                    <span style={{ marginLeft: '8px', fontSize: '14px', color: isDarkMode ? '#90cdf4' : '#2b6cb0', fontWeight: '500' }}>
                      {blank.correctAnswer}
                    </span>
                  </div>
                ))}
              </div>
            )}
            
            {currentQuestionResult.explanation && (
              <div style={{ 
                padding: '12px',
                background: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
                borderRadius: '8px'
              }}>
                <p style={{ margin: '0 0 6px 0', fontSize: '13px', fontWeight: '600', color: isDarkMode ? '#e2e8f0' : '#2d3748' }}>
                  Explanation:
                </p>
                <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: isDarkMode ? '#a0aec0' : '#4a5568' }}>
                  <FormattedText text={currentQuestionResult.explanation} />
                </p>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setShowingFeedback(false);
              setCurrentQuestionResult(null);
              setCurrentQuestion(currentQuestion - 1);
            }}
            disabled={currentQuestion === 0 || (quiz.showAnswersMode === 'immediate' && showingFeedback)}
            style={{ flex: 1, fontSize: '12px', padding: '10px' }}
          >
            ← Previous
          </button>

          {currentQuestion === quiz.questions.length - 1 ? (
            <button
              className="btn btn-primary"
              onClick={() => {
                if (quiz.showAnswersMode === 'immediate' && !showingFeedback && quiz.questions[currentQuestion].questionType !== 'Essay') {
                  // Show feedback first for last question
                  checkCurrentAnswer();
                } else {
                  // Submit the quiz
                  handleSubmit();
                }
              }}
              disabled={submitting || loading || Object.keys(answers).length !== quiz.questions.length}
              style={{ 
                flex: 2, 
                fontSize: '12px', 
                padding: '10px',
                opacity: submitting || loading || Object.keys(answers).length !== quiz.questions.length ? 0.6 : 1,
                cursor: submitting || loading || Object.keys(answers).length !== quiz.questions.length ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? '⏳ Submitting...' : 
               (quiz.showAnswersMode === 'immediate' && showingFeedback) ? '✓ Submit Quiz' :
               '✓ Submit Quiz'}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleNextQuestion}
              style={{ flex: 1, fontSize: '12px', padding: '10px' }}
            >
              {(quiz.showAnswersMode === 'immediate' && showingFeedback) ? 'Continue →' : 'Next →'}
            </button>
          )}
        </div>

        {Object.keys(answers).length !== quiz.questions.length && (
          <p style={{ marginTop: '12px', color: isDarkMode ? '#fbd38d' : '#d69e2e', textAlign: 'center', fontSize: '13px', fontWeight: '500' }}>
            ⚠️ Please answer all questions before submitting
          </p>
        )}
      </div>
    </div>
  );
}

export default TakeQuiz;
